import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { ArrowLeft, Save, Loader2 } from 'lucide-react'
import { apiGet } from '../lib/api'

interface CategoriaNode {
  id: string
  nombre: string
  slug: string
  padreId: string
  nivel: number
}

interface CategoriasTreeData {
  total: number
  data: CategoriaNode[]
}

interface CategoriaDetail {
  id: string
  nombre: string
  slug: string
  padreId: string
  descripcion: string
  imagen: string
  orden: number
  destacada: boolean
  muestraHome: boolean
  metaTitle: string
  metaDescription: string
  metaKeywords: string
  activa: boolean
}

interface FormState {
  name: string
  slug: string
  parent_id: string
  description: string
  image: string
  sort_order: number
  is_featured: boolean
  show_on_home: boolean
  meta_title: string
  meta_description: string
  meta_keywords: string
}

const empty: FormState = {
  name: '',
  slug: '',
  parent_id: '',
  description: '',
  image: '',
  sort_order: 0,
  is_featured: false,
  show_on_home: false,
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

export function CategoriaForm() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // useParams es seguro porque la ruta puede ser /categorias/nueva o /categorias/:id
  const params = useParams({ strict: false }) as { id?: string }
  const idParam = params.id
  const isEdit = idParam && idParam !== 'nueva'
  const [form, setForm] = useState<FormState>(empty)
  const [slugTouched, setSlugTouched] = useState(false)
  const [error, setError] = useState<string>('')

  const treeQuery = useQuery<CategoriasTreeData>({
    queryKey: ['categorias-tree-select'],
    queryFn: () => apiGet('/api/admin/categorias?tree=1'),
  })

  const detailQuery = useQuery<CategoriaDetail>({
    queryKey: ['categoria', idParam],
    queryFn: () => apiGet(`/api/admin/categorias/${idParam}`),
    enabled: !!isEdit,
  })

  useEffect(() => {
    if (detailQuery.data) {
      const d = detailQuery.data
      setForm({
        name: d.nombre,
        slug: d.slug,
        parent_id: d.padreId,
        description: d.descripcion,
        image: d.imagen,
        sort_order: d.orden,
        is_featured: d.destacada,
        show_on_home: d.muestraHome,
        meta_title: d.metaTitle,
        meta_description: d.metaDescription,
        meta_keywords: d.metaKeywords,
      })
      setSlugTouched(true)
    }
  }, [detailQuery.data])

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((s) => ({ ...s, [k]: v }))
  }

  function onChangeName(v: string) {
    update('name', v)
    if (!slugTouched && !isEdit) {
      update('slug', slugify(v))
    }
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name,
        slug: form.slug,
        parent_id: form.parent_id || null,
        description: form.description || null,
        image: form.image,
        sort_order: Number(form.sort_order) || 0,
        is_featured: form.is_featured,
        show_on_home: form.show_on_home,
        meta_title: form.meta_title,
        meta_description: form.meta_description || null,
        meta_keywords: form.meta_keywords || null,
      }
      const url = isEdit ? `/api/admin/categorias/${idParam}` : '/api/admin/categorias'
      const method = isEdit ? 'PUT' : 'POST'
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: 'Error' }))
        throw new Error(err.error || 'Error guardando')
      }
      return r.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categorias-tree'] })
      queryClient.invalidateQueries({ queryKey: ['categorias-tree-select'] })
      navigate({ to: '/categorias' })
    },
    onError: (e: Error) => {
      setError(e.message)
    },
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!form.name.trim()) {
      setError('El nombre es obligatorio')
      return
    }
    if (!/^[a-z0-9-]+$/.test(form.slug)) {
      setError('El slug solo puede contener letras minúsculas, números y guiones')
      return
    }
    mutation.mutate()
  }

  const title = isEdit ? 'Editar categoría' : 'Nueva categoría'
  const treeOptions = (treeQuery.data?.data ?? []).filter((c) => c.id !== idParam)

  if (isEdit && detailQuery.isLoading) {
    return (
      <>
        <Topbar title={title} />
        <div className="grid place-items-center p-20 text-ink-500">
          <Loader2 className="size-8 animate-spin" />
        </div>
      </>
    )
  }

  return (
    <>
      <Topbar
        title={title}
        subtitle={isEdit ? `ID: ${idParam}` : 'Crear una nueva categoría en el árbol'}
        actions={
          <button
            type="button"
            onClick={() => navigate({ to: '/categorias' })}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Volver
          </button>
        }
      />
      <form onSubmit={onSubmit} className="p-6">
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Información básica</h2>
              <div className="space-y-4">
                <Field label="Nombre" required>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => onChangeName(e.target.value)}
                    required
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>

                <Field label="Slug" required hint="URL amigable, ej: bujias-y-cables">
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => {
                      setSlugTouched(true)
                      update('slug', e.target.value)
                    }}
                    required
                    pattern="[a-z0-9-]+"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-sm outline-none focus:border-brand-500"
                  />
                </Field>

                <Field label="Categoría padre" hint="Vacío si es categoría raíz">
                  <select
                    value={form.parent_id}
                    onChange={(e) => update('parent_id', e.target.value)}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  >
                    <option value="">— Sin padre (raíz) —</option>
                    {treeOptions.map((c) => (
                      <option key={c.id} value={c.id}>
                        {'— '.repeat(c.nivel)}
                        {c.nombre}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Descripción">
                  <textarea
                    value={form.description}
                    onChange={(e) => update('description', e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>

                <Field label="Imagen (URL)">
                  <input
                    type="text"
                    value={form.image}
                    onChange={(e) => update('image', e.target.value)}
                    placeholder="https://…"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
              </div>
            </div>

            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">SEO</h2>
              <div className="space-y-4">
                <Field label="Meta Title">
                  <input
                    type="text"
                    value={form.meta_title}
                    onChange={(e) => update('meta_title', e.target.value)}
                    maxLength={150}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
                <Field label="Meta Description">
                  <textarea
                    value={form.meta_description}
                    onChange={(e) => update('meta_description', e.target.value)}
                    rows={2}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
                <Field label="Meta Keywords">
                  <input
                    type="text"
                    value={form.meta_keywords}
                    onChange={(e) => update('meta_keywords', e.target.value)}
                    placeholder="palabra1, palabra2"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <h2 className="mb-4 text-sm font-semibold text-ink-900">Opciones</h2>
              <div className="space-y-4">
                <Field label="Orden de visualización">
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => update('sort_order', parseInt(e.target.value) || 0)}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                  />
                </Field>

                <label className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={form.is_featured}
                    onChange={(e) => update('is_featured', e.target.checked)}
                    className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  />
                  Categoría destacada
                </label>
                <label className="flex items-center gap-2 text-sm text-ink-700">
                  <input
                    type="checkbox"
                    checked={form.show_on_home}
                    onChange={(e) => update('show_on_home', e.target.checked)}
                    className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  />
                  Mostrar en home
                </label>
              </div>
            </div>

            <div className="rounded-2xl border border-ink-200 bg-white p-6">
              <button
                type="submit"
                disabled={mutation.isPending}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {mutation.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                {isEdit ? 'Guardar cambios' : 'Crear categoría'}
              </button>
              <button
                type="button"
                onClick={() => navigate({ to: '/categorias' })}
                className="mt-2 w-full rounded-lg border border-ink-200 bg-white px-4 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  )
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-ink-700">
        {label} {required && <span className="text-red-600">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-ink-500">{hint}</p>}
    </div>
  )
}
